import { type FC, useCallback, useEffect, useMemo } from 'react';
import { Flex, Link, SimpleGrid } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { ShippingOptionType } from '../../../../bff-api/baskets/model';
import { ClientOnly } from '../../../../components/client-only/ClientOnly';
import { type Block, Blocks } from '../../../../components/sfcc-content-rendering/Blocks';
import { StorageKey } from '../../../../hooks/model/StorageKey';
import { Button } from '../../../../ui-components/button';
import { Text } from '../../../../ui-components/typography/text';
import { classNames } from '../../../../utils/tailwind/classNames';
import { isServer } from '../../../../utils/utils';
import { BloomreachProductType } from '../../hooks/use-search';
import { getProductTileId } from '../../utils/search-page-utils';
import { ProductTileRecipe } from '../product-tile';
import { ProductTileCulinary } from '../product-tile/culinary/ProductTileCulinary';
import { ProductTileHardGood } from '../product-tile/hardgood/ProductTileHardGood';
import { ProductTileSkeleton } from '../skeletons/ProductTileSkeleton';
import { SearchListingNoResults } from './SearchListingNoResults';
import type { GTMSourceEvent } from '../../../../hooks/gtm/model/events/GTMSourceEvent';
import type { SearchProduct, UseSearchResult } from '../../hooks/use-search';
import type { ImageProps } from '../product-image';

export type SearchProductTileProps = SearchProduct &
    ImageProps & {
        onClick?: () => void;
        shippingOptionType?: ShippingOptionType;
        source: GTMSourceEvent;
    };

const componentByProductType: Record<BloomreachProductType, FC<SearchProductTileProps>> = {
    [BloomreachProductType.hardgood]: ProductTileHardGood,
    [BloomreachProductType.culinary]: ProductTileCulinary,
    [BloomreachProductType.recipe]: ProductTileRecipe,
};

// TODO: on load these many images are shown depending on the breakpoint: base: 2, sm: 6, md: 4, lg-sm: 8, lg: 8
// however, in SSR, we don't have guarantee of which page was cached so using useIsMobile will potentially not
// return the correct breakpoint, thus changing it at hydration. It is observed that 8 images are shown in the
// biggest device, so for now it will be hardcoded.
const DEFAULT_IMAGE_EAGER_LOAD = 8;
const SKELETON_COUNT = 12;
const skeletonItems = Array.from(
    { length: SKELETON_COUNT },
    (_, index) => `tile-skeleton-${index}`,
);

interface SearchListingProps {
    id: string;
    search: UseSearchResult;
    pagePlacementBlocks?: Map<number, Block[]>;
    source: GTMSourceEvent;
}

const STORAGE_CLEANUP_DELAY = 500;
const SCROLL_RESTORATION_DELAY = 100;

export const SearchListing: FC<SearchListingProps> = ({
    id,
    search,
    pagePlacementBlocks = new Map<number, Block[]>(),
    source,
}) => {
    const {
        hasPreviousPage,
        hasNextPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isFetching,
        isPreviousData,
        total,
        items,
        handleNextPage,
        nextPageUrl,
        handlePreviousPage,
        searchParams,
    } = search;

    const shippingOptionType = useMemo(() => {
        if (searchParams.bopisStoreId) {
            return ShippingOptionType.BOPIS;
        }
        if (!!searchParams.sddStoreId && searchParams.sddStoreId !== 'none') {
            return ShippingOptionType.SDD;
        }
        return ShippingOptionType.SHIP_TO;
    }, [searchParams.bopisStoreId, searchParams.sddStoreId]);

    const onClick = useCallback((productId: string): void => {
        sessionStorage.setItem(StorageKey.PLP_PRODUCT_ID_SELECTED, productId);
    }, []);

    useEffect(() => {
        const productId = sessionStorage.getItem(StorageKey.PLP_PRODUCT_ID_SELECTED);

        if (!productId) {
            return;
        }

        // Delay removal to handle React Strict Mode double-mount or rapid re-renders
        // ensuring subsequent renders still see the key and maintain 'isRestoringScrollPosition' as true.
        setTimeout(() => {
            sessionStorage.removeItem(StorageKey.PLP_PRODUCT_ID_SELECTED);
        }, STORAGE_CLEANUP_DELAY);

        const tileId = getProductTileId(productId);
        const productTile = document.getElementById(tileId);

        if (productTile) {
            // Delay scrolling to ensure layout is fully painted and browser native restoration is done
            setTimeout(() => {
                try {
                    productTile.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } catch (error) {
                    // Silently handle scroll errors (e.g., element not in viewport, browser restrictions)
                    // The important part is that we've already removed the item from sessionStorage
                }
            }, SCROLL_RESTORATION_DELAY);
        }
    }, []);

    // We don't want this value to change on rerenders since the value
    // will no longer be there after the useEffect above runs
    const isRestoringScrollPosition = useMemo(
        () => (!isServer ? !!sessionStorage.getItem(StorageKey.PLP_PRODUCT_ID_SELECTED) : false),
        [],
    );

    const itemsWithBlocks = useMemo<(SearchProduct | Block[])[]>(() => {
        if (hasPreviousPage) {
            // if has previous page we haven't added the first page yet
            return items;
        }
        const result: (SearchProduct | Block[])[] = [];
        let currentPosition: number = 1;
        items.forEach((item: SearchProduct) => {
            const positionBlocks = pagePlacementBlocks.get(currentPosition);
            if (positionBlocks?.length) {
                result.push(positionBlocks);
                currentPosition += 1;
            }

            result.push(item);
            currentPosition += 1;
        });
        return result;
    }, [hasPreviousPage, items, pagePlacementBlocks]);

    const renderItem = useCallback(
        (item: SearchProduct | Block[], idx: number) => {
            if ('productType' in item) {
                const Component = componentByProductType[item.productType];
                const props: SearchProductTileProps = {
                    ...item,
                    loading:
                        idx >= DEFAULT_IMAGE_EAGER_LOAD && !isRestoringScrollPosition
                            ? 'lazy'
                            : undefined,
                    fetchPriority: idx === 0 ? 'high' : undefined,
                    onClick: () => onClick(item.id),
                    shippingOptionType,
                    source,
                };
                return <Component key={item.id} {...props} />;
            }
            const blockId = `page-placement-block-${item.map((block) => block.id).join(',')}`;
            return (
                <div key={blockId} className="flex w-full justify-center">
                    <Blocks contentBlocks={{ [blockId]: item }} id={blockId} />
                </div>
            );
        },
        [onClick, shippingOptionType, isRestoringScrollPosition, source],
    );

    const { eagerLoadItems, lazyLoadItems } = useMemo(() => {
        if (isRestoringScrollPosition) {
            return { eagerLoadItems: itemsWithBlocks, lazyLoadItems: [] };
        }
        const _eagerLoadItems = itemsWithBlocks.slice(0, DEFAULT_IMAGE_EAGER_LOAD);
        const _lazyLoadItems = itemsWithBlocks.slice(DEFAULT_IMAGE_EAGER_LOAD);
        return { eagerLoadItems: _eagerLoadItems, lazyLoadItems: _lazyLoadItems };
    }, [isRestoringScrollPosition, itemsWithBlocks]);

    if (items.length === 0) {
        return <SearchListingNoResults id={id} search={search} />;
    }

    // Show skeletons when fetching new search data (e.g., changing categories)
    // but keep previous data visible for tags/filters (keepPreviousData behavior)
    const isFetchingNewSearch =
        isFetching && isPreviousData && !isFetchingNextPage && !isFetchingPreviousPage;

    return (
        <Flex direction="column" data-testid={`product-grid-container-${id}`} className="pt-2">
            {hasPreviousPage ? (
                <header
                    data-testid={`product-grid-header-${id}`}
                    className="mb-6 flex w-full flex-col items-center justify-center gap-6"
                >
                    <Flex justify="center" align="center">
                        <Button
                            id="product-grid-header-show-previous-button"
                            onClick={handlePreviousPage}
                            isDisabled={!hasPreviousPage || isFetchingPreviousPage}
                            isLoading={isFetchingPreviousPage}
                            variant="secondary"
                        >
                            <FormattedMessage
                                id="search.pagination.show-previous"
                                defaultMessage="Show Previous"
                            />
                        </Button>
                    </Flex>
                </header>
            ) : null}
            <SimpleGrid
                columns={{ base: 2, sm: 3, md: 4 }}
                columnGap={{ base: 3, sm: 6 }}
                rowGap={8}
                alignItems="stretch"
                data-testid={`product-grid-body-${id}`}
                className={classNames(
                    // Even children - product tile image margins
                    '[&>*:nth-child(even)_*.product-tile-image]:mr-4',
                    '[&>*:nth-child(even)_*.product-tile-image]:sm:mr-4',
                    'md:[&>*:nth-child(even)_*.product-tile-image]:mr-0',
                    // Even children - product tile info margins
                    '[&>*:nth-child(even)_*.product-tile-info]:mr-4',
                    '[&>*:nth-child(even)_*.product-tile-info]:sm:mr-4',
                    'md:[&>*:nth-child(even)_*.product-tile-info]:mr-0',
                    // Odd children - add to bag button margins
                    '[&>*:nth-child(odd)_*.product-tile-atb-button]:ml-4',
                    '[&>*:nth-child(odd)_*.product-tile-atb-button]:sm:ml-4',
                    // Odd children - product tile image margins
                    '[&>*:nth-child(odd)_*.product-tile-image]:ml-4',
                    'sm:[&>*:nth-child(odd)_*.product-tile-image]:ml-0',
                    // Odd children - product tile info margins
                    '[&>*:nth-child(odd)_*.product-tile-info]:ml-4',
                    'sm:[&>*:nth-child(odd)_*.product-tile-info]:ml-0',
                )}
            >
                {/* When fetching new search data, show skeletons instead of previous items */}
                {isFetchingNewSearch ? (
                    skeletonItems.map((skeletonId) => (
                        <ProductTileSkeleton
                            key={`fetching-${skeletonId}`}
                            productType={search.productType}
                        />
                    ))
                ) : (
                    <>
                        {/* Render items */}
                        {eagerLoadItems.map((item, idx) => renderItem(item, idx))}
                        <ClientOnly hideBelowTheFold>
                            {lazyLoadItems.map((item, idx) =>
                                renderItem(item, idx + DEFAULT_IMAGE_EAGER_LOAD),
                            )}
                        </ClientOnly>
                    </>
                )}

                {/* Loading next page: show skeletons where new items will appear */}
                {isFetchingNextPage &&
                    skeletonItems.map((skeletonId) => (
                        <ProductTileSkeleton
                            key={`next-${skeletonId}`}
                            productType={search.productType}
                        />
                    ))}
            </SimpleGrid>
            <footer
                data-testid={`product-grid-footer-${id}`}
                className="mt-8 flex w-full flex-col items-center justify-center"
            >
                <Flex justify="center" align="center" direction="column" gap={3}>
                    <Text
                        color="slt-pepper.1000"
                        data-testid="product-grid-footer-pagination-summary"
                    >
                        <FormattedMessage
                            id="search.pagination.summary"
                            defaultMessage="Showing {items} of {total}"
                            values={{
                                items: itemsWithBlocks.length,
                                total: total + pagePlacementBlocks.size,
                            }}
                        />
                    </Text>
                    {hasNextPage ? (
                        <Button
                            id="product-grid-footer-show-more-button"
                            variant="primary-black"
                            onClick={handleNextPage}
                            isDisabled={!hasNextPage || isFetchingNextPage}
                            isLoading={isFetchingNextPage}
                        >
                            <Link
                                href={nextPageUrl}
                                _hover={{
                                    textDecoration: 'none',
                                }}
                                _active={{
                                    textDecoration: 'none',
                                }}
                                _focus={{
                                    textDecoration: 'none',
                                }}
                            >
                                <FormattedMessage
                                    id="search.pagination.show-more"
                                    defaultMessage="Show More"
                                />
                            </Link>
                        </Button>
                    ) : null}
                </Flex>
            </footer>
        </Flex>
    );
};

SearchListing.defaultProps = {
    pagePlacementBlocks: new Map(),
};
